// ============================================================================
// FILE: src/modules/uploads/components/UploadDrawer.jsx
// MODULE: Generic Upload Framework
//
// PURPOSE:
// Generic Upload Wizard Controller.
//
// RESPONSIBILITIES
// - Maintain Wizard State
// - Call Upload APIs
// - Handle Navigation
// - Handle Polling
// - Render Upload Steps
//
// NOTES
// - Acts as Controller.
// - Step Components contain UI only.
// - No business logic.
// ============================================================================
//
// AUTHOR:
// DizyPro ERP Development Team
// ============================================================================

import React, {
    useEffect,
    useRef,
    useState
} from "react";

import uploadService
    from "../services/uploadService";

import {

    FIRST_STEP,

    LAST_STEP,

    UPLOAD_STEPS,

    STATUS_IMPORTING,

    STATUS_COMPLETED,

    STATUS_FAILED,
    STEP_UPLOAD,

    STEP_VALIDATE,

    STEP_IMPORT,

    STEP_COMPLETE

} from "../constants/uploadConstants";

import ConfigureStep
    from "./wizard/ConfigureStep";

import TemplateStep
    from "./wizard/TemplateStep";

import UploadStep
    from "./wizard/UploadStep";

import ValidateStep
    from "./wizard/ValidateStep";

import ImportStep
    from "./wizard/ImportStep";

import CompleteStep
    from "./wizard/CompleteStep";

import "../styles/upload.css";

const POLLING_INTERVAL = 3000;

// ============================================================================
// Component
// ============================================================================

const UploadDrawer = ({

    open,

    onClose,

    moduleCode,

    moduleName,

    entityCode,

    entityName,

    title,

    description,

    uploadTypes = []

}) => {

    // ======================================================================
    // Wizard Navigation
    // ======================================================================

    const [

        currentStep,

        setCurrentStep


    ] = useState(FIRST_STEP);

    // ======================================================================
    // Drawer
    // ======================================================================

    const [

        maximized,

        setMaximized

    ] = useState(false);

    // ======================================================================
    // Runtime
    // ======================================================================

    const pollingTimer = useRef(null);

    // ======================================================================
    // Wizard State
    // ======================================================================

    const [

        wizard,

        setWizard

    ] = useState({

        moduleCode,
        moduleName,
        entityCode,

        entityName,

        title,

        description,

        uploadTypes,

        selectedUploadType: "",

        selectedFile: null,

        upload: {

            job: null,

            validation: null,

            import: null

        },

        loading: false,

        error: null

    });

    // ======================================================================
    // Visibility
    // ======================================================================

    

    // ======================================================================
    // Drawer
    // ======================================================================

    const handleClose = () => {

        stopPolling();

        if (onClose) {

            onClose();

        }

    };

    const handleMaximize = () => {

        setMaximized(true);

    };

    const handleRestore = () => {

        setMaximized(false);

    };

    // ======================================================================
    // Navigation
    // ======================================================================

    const handleNext = () => {

        if (currentStep < LAST_STEP) {

            setCurrentStep(

                currentStep + 1

            );

        }

    };

    const handlePrevious = () => {

        if (currentStep > FIRST_STEP) {

            setCurrentStep(

                currentStep - 1

            );

        }

    };

    // ======================================================================
    // Configure
    // ======================================================================

    const handleUploadTypeChange = (

        uploadType

    ) => {

        setWizard(

            previous => ({

                ...previous,

                selectedUploadType: uploadType

            })

        );

    };

    // ======================================================================
    // Upload
    // ======================================================================

    const handleFileChange = (

        file

    ) => {

        setWizard(

            previous => ({

                ...previous,

                selectedFile: file

            })

        );

    };

    

    // ======================================================================
    // Template
    // ======================================================================

    

    // ======================================================================
    // Import
    // ======================================================================

    

    // ======================================================================
    // Downloads
    // ======================================================================

    

    

    // ======================================================================
    // Polling
    // ======================================================================

    

    const stopPolling = () => {

        if (

            pollingTimer.current

        ) {

            clearInterval(

                pollingTimer.current

            );

            pollingTimer.current = null;

        }

    };

    

    // ======================================================================
    // Cleanup
    // ======================================================================

    useEffect(() => {

        return () => {

            stopPolling();

        };

    }, []);
        // ======================================================================
    // Upload
    // ======================================================================

    const handleUpload = async () => {

        try {

            if (!wizard.selectedUploadType) {

                setWizard(previous => ({

                    ...previous,

                    error: "Please select Upload Type."

                }));

                return;

            }

            if (!wizard.selectedFile) {

                setWizard(previous => ({

                    ...previous,

                    error: "Please select an Excel file."

                }));

                return;

            }

            setWizard(previous => ({

                ...previous,

                loading: true,

                error: null

            }));

            const response = await uploadService.uploadFile({

                moduleCode: wizard.moduleCode,

                entityName: wizard.entityName,

                uploadType: wizard.selectedUploadType,

                file: wizard.selectedFile

            });

            const job = response.upload_job;

            const validation = {
                total_rows: job?.total_rows ?? 0,

                successful_rows:
                    job?.validated_rows ?? 0,

                failed_rows:
                    job?.failed_validation_rows ?? 0
            };

            setWizard(previous => ({
                ...previous,

                loading: false,

                upload: {
                    ...previous.upload,

                    job,

                    validation,

                    import: {
                        ...job
                    }
                }
            }));

            if (
                (job?.failed_validation_rows ?? 0) > 0
            ) {
                setCurrentStep(STEP_VALIDATE);
            } else {
                setCurrentStep(STEP_IMPORT);
            }

        }

        catch (error) {

            setWizard(previous => ({

                ...previous,

                loading: false,

                error:

                    error.message ||

                    "Upload failed."

            }));

        }

    };

    // ======================================================================
    // Template
    // ======================================================================

    const handleDownloadTemplate =
        async () => {

        try {

            await uploadService.downloadTemplate({

                moduleCode:

                    wizard.moduleCode,

                entityName:

                    wizard.entityName,

                uploadType:

                    wizard.selectedUploadType

            });

        }

        catch (error) {

            setWizard(previous => ({

                ...previous,

                error:

                    error.message ||

                    "Unable to download template."

            }));

        }

    };

    // ======================================================================
    // Import
    // ======================================================================

    const handleImport =
        async () => {

        try {

            setWizard(previous => ({

                ...previous,

                loading: true

            }));

            const response =

                await uploadService.importUpload(

                    wizard.upload.job.id

                );

            setWizard(previous => ({

                ...previous,

                upload: {

                    ...previous.upload,

                    import: response

                }

            }));

            setCurrentStep(STEP_IMPORT);

            startPolling();

        }

        catch (error) {

            setWizard(previous => ({

                ...previous,

                loading: false,

                error:

                    error.message ||

                    "Import failed."

            }));

        }

    };

    // ======================================================================
    // Downloads
    // ======================================================================

    const handleDownloadErrorFile =
        async () => {

        if (

            !wizard.upload.job

        ) {

            return;

        }

        await uploadService.downloadErrorFile(

            wizard.upload.job.id

        );

    };

    const handleDownloadSuccessFile =
        async () => {

        if (

            !wizard.upload.job

        ) {

            return;

        }

        await uploadService.downloadSuccessFile(

            wizard.upload.job.id

        );

    };

    // ======================================================================
    // Re-upload
    // ======================================================================

    const handleReUpload = () => {

        setWizard(previous => ({

            ...previous,

            selectedFile: null,

            upload: {

                job: null,

                validation: null,

                import: null

            }

        }));

        setCurrentStep(STEP_UPLOAD);

    };
        // ======================================================================
    // Polling
    // ======================================================================

    const startPolling = () => {

        stopPolling();

        pollingTimer.current = setInterval(

            refreshUploadStatus,

            POLLING_INTERVAL

        );

    };

    const refreshUploadStatus =
        async () => {

        try {

            if (

                !wizard.upload.job

            ) {

                return;

            }

            const response =

                await uploadService.getUploadStatus(

                    wizard.upload.job.id

                );

            setWizard(previous => ({

                ...previous,

                upload: {

                    ...previous.upload,

                    import: response

                }

            }));

            if (

                response.status === STATUS_COMPLETED

            ) {

                stopPolling();

                setCurrentStep(STEP_COMPLETE);

            }

            if (

                response.status === STATUS_FAILED

            ) {

                stopPolling();

            }

        }

        catch (error) {

            stopPolling();

            setWizard(previous => ({

                ...previous,

                error:

                    error.message ||

                    "Unable to refresh upload status."

            }));

        }

    };

    // ======================================================================
    // Step Renderer
    // ======================================================================

    const renderCurrentStep = () => {

        switch (currentStep) {

            case 1:

                return (

                    <ConfigureStep

                        wizard={wizard}

                        onUploadTypeChange={

                            handleUploadTypeChange

                        }

                    />

                );

            case 2:

                return (

                    <TemplateStep

                        wizard={wizard}

                        onDownloadTemplate={

                            handleDownloadTemplate

                        }

                    />

                );

            case 3:

                return (

                    <UploadStep

                        wizard={wizard}

                        onFileChange={

                            handleFileChange

                        }

                        onUpload={

                            handleUpload

                        }

                    />

                );

            case 4:

                return (

                    <ValidateStep

                        wizard={wizard}

                        onImport={

                            handleImport

                        }

                        onDownloadErrors={

                            handleDownloadErrorFile

                        }

                        onReUpload={

                            handleReUpload

                        }

                    />

                );

            case 5:

                return (

                    <ImportStep

                        wizard={wizard}

                    />

                );

            case 6:

                return (

                    <CompleteStep

                        wizard={wizard}

                        onDownloadSuccess={

                            handleDownloadSuccessFile

                        }

                        onClose={

                            handleClose

                        }

                    />

                );

            default:

                return null;

        }

    };

    // ======================================================================
    // Primary Button
    // ======================================================================

    const renderPrimaryButton = () => {

        switch (currentStep) {

            case 1:

            case 2:

                return (

                    <button
                        className="btn btn-primary"
                        onClick={handleNext}
                    >
                        Next
                    </button>

                );

            case 3:

                return (

                    <button
                        className="btn btn-primary"
                        onClick={handleUpload}
                    >
                        Upload & Validate
                    </button>

                );

            case 4:

                return (

                    <button
                        className="btn btn-primary"
                        onClick={handleImport}
                        disabled={
                            wizard.upload.validation
                                ?.failed_rows > 0
                        }
                    >
                        Import
                    </button>

                );

            case 5:

                return (

                    <button
                        className="btn btn-secondary"
                        disabled
                    >
                        Processing...
                    </button>

                );

            case 6:

                return (

                    <button
                        className="btn btn-success"
                        onClick={handleClose}
                    >
                        Close
                    </button>

                );

            default:

                return null;

        }
    };
        // ======================================================================
    // Secondary Button
    // ======================================================================

    const renderSecondaryButton = () => {

        switch (currentStep) {

            case 1:

                return (

                    <button
                        className="btn btn-outline-secondary"
                        onClick={handleClose}
                    >
                        Cancel
                    </button>

                );

            case 2:

            case 3:

            case 4:

                return (

                    <button
                        className="btn btn-outline-secondary"
                        onClick={handlePrevious}
                    >
                        Previous
                    </button>

                );

            case 5:

                return (

                    <button
                        className="btn btn-outline-secondary"
                        disabled
                    >
                        Previous
                    </button>

                );

            case 6:

                return null;

            default:

                return null;

        }

    };

    // ======================================================================
    // Upload Summary
    // ======================================================================

    const renderUploadSummary = () => (

        <div className="upload-summary">

            <h5>Upload Summary</h5>

            <div className="summary-item">
                <label>Module</label>
                <span>{wizard.moduleName}</span>
            </div>

            <div className="summary-item">
                <label>Entity</label>
                <span>{wizard.entityName}</span>
            </div>

            <div className="summary-item">
                <label>Upload Type</label>
                <span>{wizard.selectedUploadType || "-"}</span>
            </div>

            <div className="summary-item">
                <label>Selected File</label>
                <span>
                    {wizard.selectedFile
                        ? wizard.selectedFile.name
                        : "-"}
                </span>
            </div>

            <div className="summary-item">
                <label>Job Number</label>
                <span>
                    {wizard.upload.job?.job_number || "-"}
                </span>
            </div>

            <div className="summary-item">
                <label>Status</label>
                <span>
                    {wizard.upload.import?.status ||
                        wizard.upload.job?.status ||
                        "-"}
                </span>
            </div>

            <div className="summary-item">
                <label>Total Rows</label>
                <span>
                    {wizard.upload.validation?.total_rows ?? 0}
                </span>
            </div>

            <div className="summary-item">
                <label>Valid Rows</label>
                <span>
                    {wizard.upload.validation?.successful_rows ?? 0}
                </span>
            </div>

            <div className="summary-item">
                <label>Error Rows</label>
                <span>
                    {wizard.upload.validation?.failed_rows ?? 0}
                </span>
            </div>

            <div className="summary-item">
                <label>Imported</label>
                <span>
                    {wizard.upload.import?.imported_rows ??

                        wizard.upload.job?.imported_rows ??

                        0}
                </span>
            </div>

        </div>

    );

    // ======================================================================
    // Render
    // ======================================================================

    if (!open) {

        return null;

    }
    return (

        <div className={`upload-drawer ${maximized ? "maximized" : ""}`}>

            <div className="upload-header">

                <div>

                    <h3>{title}</h3>

                    <small>{description}</small>

                </div>

                <div className="upload-header-actions">

                    {

                        maximized ? (

                            <button
                                className="btn btn-light"
                                onClick={handleRestore}
                            >
                                Restore
                            </button>

                        ) : (

                            <button
                                className="btn btn-light"
                                onClick={handleMaximize}
                            >
                                Maximize
                            </button>

                        )

                    }

                    <button
                        className="btn btn-danger"
                        onClick={handleClose}
                    >
                        Close
                    </button>

                </div>

            </div>

            <div className="upload-stepper">

                {

                    UPLOAD_STEPS.map(step => (

                        <div

                            key={step.id}

                            className={

                                currentStep === step.id
                                    ? "step active"
                                    : currentStep > step.id
                                        ? "step completed"
                                        : "step"

                            }

                        >

                            {step.title}

                        </div>

                    ))

                }

            </div>

            <div className="upload-body">

                <div className="upload-content">

                    {renderCurrentStep()}

                </div>

                {renderUploadSummary()}

            </div>

            <div className="upload-footer">

                {renderSecondaryButton()}

                <div className="footer-spacer" />

                {renderPrimaryButton()}

            </div>

        </div>

    );

};
    
    
export default UploadDrawer;